import {
  ActionIcon,
  Box,
  Card,
  Center,
  Group,
  Input,
  Loader,
  Select,
  Stack,
  Table,
  type TableTdProps,
  Text,
  TextInput,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconChevronRight, IconSearch } from '@tabler/icons-react';
import { useGroup } from 'app/Private/GroupGate';
import { PageTitle } from 'components';
import { TenantFilter } from 'components/TenantFilter';
import { usePagination } from 'hooks/use-pagination';
import { useCompanyConfiguration } from 'init/use-company-configuration';
import { useProfile } from 'init/use-enforce-profile';
import { i18nKeys, useTranslation } from 'locales';
import { Fragment, useRef, useState } from 'react';
import { isDefined, isNonNullish } from 'remeda';
import { type GroupDebtor, useLoadGroupDebtors } from 'shared/hooks/use-load-group-debtors';
import { useLocalizedCurrencyFormatter } from 'shared/utils/currency';
import type { CurrencyCode } from 'types/currency';

const SEARCH_DEBOUNCE_MS = 300;

const NOTHING_TO_SHOW = '—';
const EMPTY_BUCKET = '–';
const NAME_MAX_WIDTH = 240;
const EVERY_COLUMN = 100;
const COMPUTED_AT_FORMAT = 'DD/MM/YYYY';
const CARD_WHITE = 'var(--mantine-color-body)';
const OPEN_CLIENT_WEIGHT = 600;
const CHEVRON_SLOT = 22;
const NAME_GAP = 6;
const COMPANY_ROW_SIZE = 13;

const AGEING_BUCKETS = [
  { bucket: 'not_due', label: i18nKeys.ANALYTICS.AGED_BALANCE.notDue },
  { bucket: '0', label: i18nKeys.ANALYTICS.AGED_BALANCE['0'] },
  { bucket: '30', label: i18nKeys.ANALYTICS.AGED_BALANCE['30'] },
  { bucket: '60', label: i18nKeys.ANALYTICS.AGED_BALANCE['60'] },
  { bucket: '90', label: i18nKeys.ANALYTICS.AGED_BALANCE['90'] },
] as const satisfies { bucket: keyof GroupDebtor['agedBalance']; label: string }[];

type OutstandingAmountProps = {
  outstanding: GroupDebtor['outstanding'];
  currency: CurrencyCode;
};

const AMOUNT_FORMAT: Intl.NumberFormatOptions = {
  currencyDisplay: 'narrowSymbol',
  trailingZeroDisplay: 'auto',
};

const OutstandingAmount = ({ outstanding, currency }: OutstandingAmountProps) => {
  const formatter = useLocalizedCurrencyFormatter(false, currency, AMOUNT_FORMAT);
  const amount = outstanding[currency];

  if (!isDefined(amount)) return null;

  return formatter.format(Number(amount));
};

const AgeingAmount = ({ outstanding, currency }: OutstandingAmountProps) =>
  Number(outstanding[currency]) === 0 ? (
    EMPTY_BUCKET
  ) : (
    <OutstandingAmount outstanding={outstanding} currency={currency} />
  );

type ComputedAtCellProps = Pick<TableTdProps, 'fz'> & {
  computedAt: GroupDebtor['agedBalanceComputedAt'];
};

type IdentifierProps = Pick<GroupDebtor, 'vatNumber' | 'siren' | 'reference'>;

const ComputedAtCell = ({ computedAt, fz }: ComputedAtCellProps) => (
  <Table.Td ta="right" fz={fz}>
    {computedAt ? computedAt.format(COMPUTED_AT_FORMAT) : NOTHING_TO_SHOW}
  </Table.Td>
);

type CompaniesToggleProps = {
  companyCount: number;
  isExpanded: boolean;
};

const CompaniesToggle = ({ companyCount, isExpanded }: CompaniesToggleProps) => {
  const { t } = useTranslation();

  return (
    <ActionIcon
      variant="subtle"
      color={isExpanded ? 'purple.6' : 'gray.6'}
      size={CHEVRON_SLOT}
      aria-label={t(i18nKeys.GROUP.COMPANY_COUNT, { count: companyCount })}
      aria-expanded={isExpanded}
    >
      <IconChevronRight size={16} style={isExpanded ? { transform: 'rotate(90deg)' } : undefined} />
    </ActionIcon>
  );
};

export const GroupClients = () => {
  const { t } = useTranslation();
  const { currentPage, pageLimit, resetPagination, PaginationFooter } = usePagination({
    indexPath: 'group/clients',
  });
  const [searchQuery, setSearchQuery] = useState('');
  const searchBoxRef = useRef<HTMLInputElement>(null);
  const [debouncedSearchQuery] = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);
  const [selectedSubdomains, setSelectedSubdomains] = useState<string[]>([]);
  const { groupDebtors, isGroupDebtorsInitialLoading, groupDebtorsError } = useLoadGroupDebtors({
    page: currentPage,
    pageLimit,
    name: debouncedSearchQuery || undefined,
    subdomains: selectedSubdomains.length ? selectedSubdomains : undefined,
  });
  const { accessibleTenants } = useProfile();
  const group = useGroup();
  const companyCurrency = useCompanyConfiguration().currency;
  const [pickedCurrency, setPickedCurrency] = useState<CurrencyCode | null>(null);
  const groupHeldCurrency =
    groupDebtors.currencies.find(held => held === companyCurrency) ?? groupDebtors.currencies.at(0);
  const currencyInView = pickedCurrency ?? groupHeldCurrency ?? companyCurrency;
  const [expandedDebtors, setExpandedDebtors] = useState<string[]>([]);
  const hasNoDebtorsToShow = groupDebtors.debtors.length === 0;

  const companyNameOf = (subdomain: string) =>
    accessibleTenants.find(tenant => tenant.subdomain === subdomain)?.companyName ?? subdomain;

  const identifierOf = ({ vatNumber, siren, reference }: IdentifierProps) => {
    if (vatNumber) return `${t(i18nKeys.VAT)} ${vatNumber}`;
    if (siren) return `SIREN ${siren}`;
    if (reference) return `${t(i18nKeys.DEBTOR.REFERENCE)} ${reference}`;
    return undefined;
  };

  const search = (query: string) => {
    setSearchQuery(query);
    resetPagination();
  };

  const toggleDebtor = (debtorId: string) =>
    setExpandedDebtors(expanded =>
      expanded.includes(debtorId)
        ? expanded.filter(expandedId => expandedId !== debtorId)
        : [...expanded, debtorId],
    );

  const groupTenants = accessibleTenants.filter(tenant => tenant.groupId === group.id);

  return (
    <Stack mih="100%" gap={0}>
      <PageTitle>
        {t(i18nKeys.CLIENTS)}
        <PageTitle.Actions>
          <TextInput
            ref={searchBoxRef}
            w={260}
            value={searchQuery}
            onChange={event => search(event.currentTarget.value)}
            placeholder={t(i18nKeys.GROUP.SEARCH_PLACEHOLDER)}
            leftSection={<IconSearch size={16} stroke={1.5} />}
            rightSection={
              searchQuery !== '' && (
                <Input.ClearButton
                  aria-label={t(i18nKeys.GROUP.CLEAR_SEARCH)}
                  onClick={() => {
                    search('');
                    searchBoxRef.current?.focus();
                  }}
                />
              )
            }
          />
          <TenantFilter
            tenants={groupTenants}
            selectedSubdomains={selectedSubdomains}
            onChange={subdomains => {
              setSelectedSubdomains(subdomains);
              resetPagination();
            }}
            w={260}
          />
          {groupDebtors.currencies.length > 1 && (
            <Select
              aria-label={t(i18nKeys.CURRENCY)}
              data={groupDebtors.currencies}
              value={currencyInView}
              onChange={picked =>
                setPickedCurrency(groupDebtors.currencies.find(known => known === picked) ?? null)
              }
              comboboxProps={{ keepMounted: false }}
              w={110}
            />
          )}
        </PageTitle.Actions>
      </PageTitle>
      <Card radius={8} p={0}>
        <Box style={{ overflowX: 'auto' }}>
          <Table horizontalSpacing="sm" styles={{ table: { whiteSpace: 'nowrap' } }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t(i18nKeys.NAME)}</Table.Th>
                <Table.Th ta="right">{t(i18nKeys.BALANCE)}</Table.Th>
                <Table.Th ta="right" title={t(i18nKeys.GROUP.WORST_PAYMENT_DELAY)}>
                  {t(i18nKeys.GROUP.WORST_PAYMENT_DELAY_SHORT)}
                </Table.Th>
                {AGEING_BUCKETS.map(({ bucket, label }) => (
                  <Table.Th key={bucket} ta="right">
                    {t(label)}
                  </Table.Th>
                ))}
                <Table.Th ta="right">{t(i18nKeys.GROUP.AGED_BALANCE_COMPUTED_AT)}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {hasNoDebtorsToShow && (
                <Table.Tr>
                  <Table.Td colSpan={EVERY_COLUMN}>
                    <Center py="xl">
                      {isGroupDebtorsInitialLoading ? (
                        <Loader aria-label={t(i18nKeys.LOADING)} />
                      ) : (
                        <Text c="dimmed">
                          {t(groupDebtorsError ? i18nKeys.ERROR.HAS_PROBLEM : i18nKeys.NO_RESULT)}
                        </Text>
                      )}
                    </Center>
                  </Table.Td>
                </Table.Tr>
              )}
              {groupDebtors.debtors.map(
                ({
                  id,
                  fullName,
                  vatNumber,
                  siren,
                  reference,
                  outstanding,
                  companies,
                  worstPaymentDelay,
                  agedBalance,
                  agedBalanceComputedAt,
                }) => {
                  const isHeldBySeveralCompanies = companies.length > 1;
                  const isExpanded = expandedDebtors.includes(id);
                  const clientWeight = isExpanded ? OPEN_CLIENT_WEIGHT : undefined;
                  const lineUnderName = [
                    ...(isHeldBySeveralCompanies
                      ? [t(i18nKeys.GROUP.COMPANY_COUNT, { count: companies.length })]
                      : companies.map(({ subdomain }) => companyNameOf(subdomain))),
                    identifierOf({ vatNumber, siren, reference }),
                  ]
                    .filter(isDefined)
                    .join(' · ');

                  return (
                    <Fragment key={id}>
                      <Table.Tr
                        onClick={isHeldBySeveralCompanies ? () => toggleDebtor(id) : undefined}
                        style={isHeldBySeveralCompanies ? { cursor: 'pointer' } : undefined}
                      >
                        <Table.Td>
                          <Group gap={NAME_GAP} wrap="nowrap">
                            {isHeldBySeveralCompanies ? (
                              <CompaniesToggle
                                companyCount={companies.length}
                                isExpanded={isExpanded}
                              />
                            ) : (
                              <Box w={CHEVRON_SLOT} />
                            )}
                            <div>
                              <Text
                                inherit
                                truncate
                                maw={NAME_MAX_WIDTH}
                                title={fullName}
                                fw={clientWeight}
                              >
                                {fullName}
                              </Text>
                              <Text
                                size="xs"
                                c="dimmed"
                                truncate
                                maw={NAME_MAX_WIDTH}
                                title={lineUnderName}
                              >
                                {lineUnderName}
                              </Text>
                            </div>
                          </Group>
                        </Table.Td>
                        <Table.Td ta="right" fw={clientWeight}>
                          <OutstandingAmount outstanding={outstanding} currency={currencyInView} />
                        </Table.Td>
                        <Table.Td ta="right">
                          {isNonNullish(worstPaymentDelay) &&
                            t(i18nKeys.ANALYTICS.DAYS_SHORT, { count: worstPaymentDelay })}
                        </Table.Td>
                        {AGEING_BUCKETS.map(({ bucket }) => (
                          <Table.Td key={bucket} ta="right">
                            <AgeingAmount
                              outstanding={agedBalance[bucket]}
                              currency={currencyInView}
                            />
                          </Table.Td>
                        ))}
                        <ComputedAtCell computedAt={agedBalanceComputedAt} />
                      </Table.Tr>
                      {isExpanded &&
                        companies.map((company, index) => (
                          <Table.Tr
                            key={company.subdomain}
                            bg={CARD_WHITE}
                            style={
                              index < companies.length - 1 ? { borderBottom: 'none' } : undefined
                            }
                          >
                            <Table.Td fz={COMPANY_ROW_SIZE}>
                              <Group gap={NAME_GAP} wrap="nowrap" pl="md">
                                <Box w={CHEVRON_SLOT} />
                                {companyNameOf(company.subdomain)}
                              </Group>
                            </Table.Td>
                            <Table.Td ta="right" fz={COMPANY_ROW_SIZE}>
                              <OutstandingAmount
                                outstanding={company.outstanding}
                                currency={currencyInView}
                              />
                            </Table.Td>
                            <Table.Td />
                            {AGEING_BUCKETS.map(({ bucket }) => (
                              <Table.Td key={bucket} ta="right" fz={COMPANY_ROW_SIZE}>
                                <AgeingAmount
                                  outstanding={company.agedBalance[bucket]}
                                  currency={currencyInView}
                                />
                              </Table.Td>
                            ))}
                            <ComputedAtCell
                              computedAt={company.agedBalanceComputedAt}
                              fz={COMPANY_ROW_SIZE}
                            />
                          </Table.Tr>
                        ))}
                    </Fragment>
                  );
                },
              )}
            </Table.Tbody>
          </Table>
        </Box>
        <PaginationFooter total={groupDebtors.pagination.totalObjects} />
      </Card>
    </Stack>
  );
};
